import { useContext } from 'react';
import { Link } from 'react-router-dom';
import Scrollbar from 'src/components/Scrollbar';
import { SidebarContext } from 'src/contexts/SidebarContext';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import { Box, Drawer, Tooltip, alpha, useTheme } from '@mui/material';
import SidebarMenu from './SidebarMenu';
import { brand } from 'src/theme/brand';
import ShopBrandMark from 'src/components/ShopBrandMark';
import { useCustomerShopBranding } from 'src/hooks/useCustomerShopBranding';

const footerButtonSx = (collapsed: boolean) => ({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: collapsed ? 'center' : 'flex-start',
  gap: 1.25,
  width: '100%',
  height: 38,
  px: collapsed ? 0 : 1.5,
  borderRadius: '10px',
  cursor: 'pointer',
  fontSize: 13.5,
  fontWeight: 500,
  color: brand.muted,
  '& svg': { fontSize: 19 },
  '&:hover': { backgroundColor: brand.subtle, color: brand.ink },
  '&:focus-visible': { outline: `2px solid ${alpha(brand.green, 0.5)}`, outlineOffset: -2 }
});

function BrandHeader({ collapsed }: { collapsed: boolean }) {
  const { isCustomer, shopName, shopLogoUrl, shopInitial } = useCustomerShopBranding();
  const theme = useTheme();

  return (
    <Box
      sx={{
        height: theme.header.height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: collapsed ? 0 : 2.5,
        flexShrink: 0
      }}
    >
      {isCustomer ? (
        <ShopBrandMark shopName={shopName} shopLogoUrl={shopLogoUrl} shopInitial={shopInitial} variant="sidebar" />
      ) : (
        <Box
          component={Link}
          to="/"
          aria-label="LumaPOS admin home"
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', overflow: 'hidden' }}
        >
          <Box
            component="img"
            src={brand.logoSrc}
            alt="LumaPOS"
            sx={{
              height: 30,
              width: collapsed ? 24 : 'auto',
              maxWidth: 150,
              objectFit: 'cover',
              objectPosition: 'left center',
              display: 'block'
            }}
          />
        </Box>
      )}
    </Box>
  );
}

function SidebarBody({ collapsed, showCollapse }: { collapsed: boolean; showCollapse: boolean }) {
  const { toggleCollapsed } = useContext(SidebarContext);

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/sign-in';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <BrandHeader collapsed={collapsed} />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Scrollbar>
          <SidebarMenu collapsed={collapsed} />
        </Scrollbar>
      </Box>
      <Box sx={{ px: collapsed ? 1.25 : 1.5, py: 1.5, borderTop: `1px solid ${brand.border}` }}>
        <Tooltip title={collapsed ? 'Sign out' : ''} placement="right">
          <Box component="button" type="button" onClick={logout} sx={footerButtonSx(collapsed)}>
            <LogoutRoundedIcon />
            {!collapsed && 'Sign out'}
          </Box>
        </Tooltip>
        {showCollapse && (
          <Tooltip title={collapsed ? 'Expand sidebar' : ''} placement="right">
            <Box
              component="button"
              type="button"
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              sx={footerButtonSx(collapsed)}
            >
              {collapsed ? <KeyboardDoubleArrowRightRoundedIcon /> : <KeyboardDoubleArrowLeftRoundedIcon />}
              {!collapsed && 'Collapse sidebar'}
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

function Sidebar() {
  const { sidebarToggle, toggleSidebar, collapsed } = useContext(SidebarContext);
  const theme = useTheme();

  return (
    <>
      <Box
        component="aside"
        sx={{
          display: { xs: 'none', lg: 'block' },
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100%',
          width: 'var(--sidebar-w)',
          zIndex: 7,
          backgroundColor: brand.surface,
          borderRight: `1px solid ${brand.border}`,
          transition: 'width .2s ease',
          overflow: 'hidden'
        }}
      >
        <SidebarBody collapsed={collapsed} showCollapse />
      </Box>
      <Drawer
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={sidebarToggle}
        onClose={toggleSidebar}
        variant="temporary"
        PaperProps={{
          sx: { width: theme.sidebar.width, backgroundColor: brand.surface, border: 'none', borderRadius: 0 }
        }}
      >
        <SidebarBody collapsed={false} showCollapse={false} />
      </Drawer>
    </>
  );
}

export default Sidebar;
